import React, { useState, useEffect, useMemo, cloneElement } from 'react';
import { NavLink, useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { DateTime } from 'luxon';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import Checkbox from '@material-ui/core/Checkbox';
import Link from '@material-ui/core/Link';
import Chip from '@material-ui/core/Chip';

import PaginatedTableHead from 'app/components/tables/PaginatedTableHead';
import PaginatedTableToolbar from 'app/components/tables/PaginatedTableToolbar';
import Loading from 'app/components/loaders/Loading';

import FormatPhoneNumber from 'app/utils/FormatPhoneNumber';

import useApp from 'app/context/useApp';
import useApi from 'app/context/useApi';
import { Highlight } from '@material-ui/icons';

const useTableStyles = makeStyles(theme => ({
    table: {
        minWidth: '100%',
    },
    tableWrapper: {
        overflowX: 'auto',
        overflowY: 'scroll'
    },
    tableCell: {
        whiteSpace: "nowrap",
        padding: "4px 10px 4px 10px",
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: 200
    },
    tableCellWrap: {
        padding: "4px 10px 4px 10px",
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: 200
    },
    noPrint: {
        '@media print' : {
            display: 'none'
        }
    },
    containerFullHeight: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'nowrap'
    },
    tableWrapperFullHeigh: {
        overflowX: 'auto',
        overflowY: 'scroll',
        flex: 1
    },
    paginationFullHeight: {
        flex: '1 0 auto',
        borderTop: '1px solid',
        borderColor: theme.palette.divider
    },
    chipPrimary: {
        backgroundColor: theme.palette.primary.main,
        color: '#ffffff',
    },
    chipSuccess: {
        backgroundColor: theme.palette.success.main,
        color: '#ffffff',
    },
    chipWarning: {
        backgroundColor: theme.palette.warning.dark,
        color: '#ffffff',
    },
    chipError: {
        backgroundColor: theme.palette.error.main,
        color: '#ffffff',
    },
    chipDefault: {},
    highlight: {
        color: theme.palette.warning.dark
    }
}));

export default function PaginatedTable(props) {

    const { app, handleApp } = useApp();

    const classes = useTableStyles();
    const api = useApi();
    const history = useHistory();

    const {
        columns,
        stateKey,
        label,
        tableUrl,
        baseUrl,
        viewUrl,
        newUrl,
        exportTitle,
        exportAllTitle,
        onImport,
        refreshBool,
        FilterDialog,
        NewDialog,
        EditDialog,
        EmailDialog,
        PaymentDialog,
        RefundDialog,
        fullHeight,
        initViewableColumns,
        initOrder,
        initOrderBy,
        onClickFn,
    } = props;

    const [ filters, setFilters ] = useState(app.user.application_state[stateKey].filters);
    const [ order, setOrder ] = useState(initOrder ?? app.user.application_state[stateKey].order);
    const [ orderBy, setOrderBy ] = useState(initOrderBy ?? app.user.application_state[stateKey].orderBy);
    const [ page, setPage ] = useState(app.user.application_state[stateKey].page);
    const [ rowsPerPage, setRowsPerPage ] = useState(app.user.application_state[stateKey].rowsPerPage);
    const [ searchColumn, setSearchColumn ] = useState(app.user.application_state[stateKey].searchColumn);
    const [ searchValue, setSearchValue ] = useState(app.user.application_state[stateKey].searchValue);
    const [ viewableColumns, setViewableColumns ] = useState(app.user.application_state[stateKey].columns);

    const [ data, setData ] = useState([]);
    const [ selected, setSelected ] = useState([]);
    const [ total, setTotal ] = useState(0);

    const [ editingId, setEditingId ] = useState(0);
    const [ editingRow, setEditingRow ] = useState(null);
    const [ editDialogOpen, setEditDialogOpen ] = useState(false);

    const [ loading, setLoading ] = useState(false);

    const getTable = () => {
        setLoading(true);

        const post = {
            filters,
            order,
            orderBy,
            page,
            rowsPerPage,
            searchColumn,
            searchValue,
            columns,
        }

        api.post('/admin/table' + tableUrl, post)
            .then(res => {
                setData(res.table.data);
                setTotal(res.table.total);
            }).finally(() => setLoading(false));
    }

    useEffect(() => {
        getTable();
    }, [
        order,
        orderBy,
        page,
        rowsPerPage,
        filters,
        refreshBool,
        columns
    ]);

    useEffect(() => {

        if (initViewableColumns && initViewableColumns.length > 0) setViewableColumns(initViewableColumns);
        if (initOrder) setOrder(initOrder);
        if (initOrderBy) setOrderBy(initOrderBy);

    }, [initViewableColumns, initOrder, initOrderBy]);

    // Memoized

    const notify = useMemo(() => {
        return columns.find(col => col.type === 'notify');
    }, [columns])

    // HANDLE SEARCHING

    const handleSearchColumnChange = event => {
        setSearchColumn(event.target.value)
        handleApp('user.application_state.'+stateKey+'.searchColumn', event.target.value)
    };

    const handleSearchValueChange = event => {
        setSearchValue(event.target.value);
        handleApp('user.application_state.'+stateKey+'.searchValue', event.target.value)
    };

    const handleSearch = () => {
        setPage(0);
        handleApp('user.application_state.'+stateKey+'.page', 0);
        getTable();
    };

    const handleRequestSort = (event, property) => {
        const newOrderBy = property;
        let newOrder = 'asc';

        if (orderBy === property && order === 'asc') newOrder = 'desc';

        setOrder(newOrder);
        setOrderBy(newOrderBy);
        handleApp('user.application_state.'+stateKey+'.order', newOrder);
        handleApp('user.application_state.'+stateKey+'.orderBy', newOrderBy);
    };

    const handleFilters = (filters) => {
        setFilters(filters);
        handleApp('user.application_state.'+stateKey+'.filters', filters)
    }

    // HANDLE SELECTING ROWS
    const handleSelect = id => {
        const i = selected.indexOf(id);

        let newSelected = [...selected];

        if (i === -1) {
            newSelected.push(id);
        } else {
            newSelected.splice(i, 1);
        }

        setSelected(newSelected);
    }

    const handleSelectAll = event => {
        if (event.target.checked) {
            const newSelected = [...selected];

            data.map(u => {
                const selectedIndex = newSelected.indexOf(u.id);

                if (selectedIndex === -1) {
                    newSelected.push(u.id);
                }
            });

            setSelected(newSelected);
        }
        else setSelected([]);
    };

    const isSelected = id => selected.indexOf(id) !== -1;

    const isHighlighted = (row) => {
        if (isSelected(row.id)) return true;

        if (notify && row.read_at === null) return true;
    }

    const isSelectAllChecked = useMemo(() => data.every(u => selected.includes(u.id)), [data, selected]);

    // HANDLE CHANGING PAGES

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        handleApp('user.application_state.'+stateKey+'.page', newPage);
    };

    const handleChangeRowsPerPage = event => {
        setRowsPerPage(event.target.value)
        handleApp('user.application_state.'+stateKey+'.rowsPerPage', event.target.value);
    };

    // HANDLE VISIBLE COLUMNS

    const toggleColumn = event => {
        const val = event.target.value;

        if (val !== null) {
            const newColumns = [...viewableColumns];
            const i = newColumns.indexOf(val);

            (i > -1) ? newColumns.splice(i, 1) : newColumns.push(val);

            setViewableColumns(newColumns);
            handleApp('user.application_state.'+stateKey+'.columns', newColumns, true);
        }
    }

    const toggleAllColumns = () => {
        const allToggledOn = (viewableColumns.length === columns.length);

        let newColumns = [];

        if (! allToggledOn) newColumns = columns.map(c => c.id);

        setViewableColumns(newColumns);
        handleApp('user.application_state.'+stateKey+'.columns', newColumns, true);
    }

    const deleteSelected = () => {
        setLoading(true);

        const post = {
            'ids': selected
        }

        api.delete( baseUrl + "/delete", { data: post})
            .then(() => {
                getTable();
                setSelected([]);
            }).finally(() => setLoading(false));
    }

    const exportSelected = () => {

        setLoading(true);

        const post = {
            order,
            orderBy,
            filters,
            viewableColumns,
            title: exportTitle,
            searchValue,
            searchColumn,
            selected,
            columns
        }

        api.post('/admin/table' + tableUrl + '/export/selected', post).finally(() => setLoading(false));
    }

    const exportAll = () => {

        setLoading(true);

        const post = {
            order,
            orderBy,
            filters,
            viewableColumns,
            title: exportAllTitle,
            searchValue,
            searchColumn,
            columns
        }

        api.post('/admin/table' + tableUrl + '/export', post).finally(() => setLoading(false));
    }

    const getTableCell = (column, n) => {

        switch (column.type) {
            case 'link':
                return (
                    <Link component={NavLink} to={column.link + n[column.link_id]}>
                        {n[column.id]}
                    </Link>
                )
            case 'email':
                return (
                    <Link target="_blank" href={"mailto:" + n[column.id]}>
                        {n[column.id]}
                    </Link>
                )
            case 'phone':
                return (
                    <Link target="_blank" href={"tel:" + n[column.id]}>
                        {FormatPhoneNumber(n[column.id])}
                    </Link>
                )
            case 'currency':
                return (
                    <span>{ Number.parseFloat(n[column.id]).toLocaleString(undefined, { style: "currency", currency: "USD" })}</span>
                )
            case 'datetime':
            case 'time':
            case 'date':
                if (n[column.id]) {
                    let now = DateTime.utc();
                    let dt = DateTime.fromSQL(n[column.id], {zone: "utc"});
                    let className = (column.highlight && (dt > now)) ? classes.highlight : null;

                    if (column.format == 'from_now') {
                        return (
                            <span className={className}>{column.pre ? column.pre : ""} {DateTime.fromSQL(n[column.id], {zone: "utc"}).toLocal().toRelative()}</span>
                        )
                    } else {
                        return (
                            <span className={className}>{DateTime.fromSQL(n[column.id], {zone: "utc"}).toLocal().toFormat(column.format)}</span>
                        )
                    }
                }
                return null;
            case 'bool':
                return (
                    <span>{n[column.id] == 1 ? 'Yes' : 'No'}</span>
                )
            case 'chip':
                return (
                    <Chip
                        className={classes[column.map[n[column.id]]]}
                        label={n[column.id]}
                    />
                )
            default:
                return (
                    <span>{n[column.id]}</span>
                )
        }
    }

    const emptyRows = rowsPerPage - data.length;

    const goTo = (e, id, row) => {
        const type = e.target.tagName;

        if (onClickFn) {
            onClickFn(row);
        } else if (type === "TD") {
            if (EditDialog) {
                setEditingId(id);
                setEditingRow(row);
                openEditDialog();
            } else {
                history.push(`${viewUrl}/${id}`);
            }
        }
    }

    // New Dialog

    const openEditDialog = () => {
        setEditDialogOpen(true);
    }

    const closeEditDialog = () => {
        setEditDialogOpen(false);
    }

    const closeEditDialogAndRefresh = () => {
        closeEditDialog();
        getTable();
    }

    const EditDialogCloned = EditDialog ? cloneElement(
        EditDialog,
        { open: editDialogOpen, onClose: closeEditDialog, onSave: closeEditDialogAndRefresh, id: editingId, data: editingRow }
    ) : null;

    return (
        <div className={fullHeight ? classes.containerFullHeight : classes.container}>
            <Loading isLoading={loading}/>
            <PaginatedTableToolbar
                data={data}
                selected={selected}
                columns={columns}
                viewUrl={viewUrl}
                newUrl={newUrl}
                viewableColumns={viewableColumns}
                searchColumn={searchColumn}
                onSearchColumnChange={handleSearchColumnChange}
                searchValue={searchValue}
                onSearchValueChange={handleSearchValueChange}
                onSearch={handleSearch}
                onToggleColumn={toggleColumn}
                onToggleAllColumns={toggleAllColumns}
                onDeleteSelected={deleteSelected}
                onExportSelected={exportSelected}
                onExportAll={exportAll}
                onImport={onImport}
                FilterDialog={FilterDialog}
                EmailDialog={EmailDialog}
                NewDialog={NewDialog}
                label={label}
                PaymentDialog={PaymentDialog}
                RefundDialog={RefundDialog}
                onFilter={handleFilters}
                filterCount={Object.keys(filters).length ?? 0}
                getTable={getTable}
            />
            <div className={classes.tableWrapper} id="paginated-table-wrapper">
                <Table
                    className={classes.table}
                    aria-labelledby="table-title"
                    stickyHeader={fullHeight}
                    >
                    <PaginatedTableHead
                        numSelected={selected.length}
                        order={order}
                        orderBy={orderBy}
                        onSelectAll={handleSelectAll}
                        onRequestSort={handleRequestSort}
                        rowCount={data.length}
                        columns={columns}
                        viewableColumns={viewableColumns}
                        isSelectAllChecked={isSelectAllChecked}
                    />
                    <TableBody>
                        {data.map(n => (
                            <TableRow
                                hover
                                role="checkbox"
                                aria-checked={isSelected(n.id)}
                                key={n.id}
                                selected={isHighlighted(n)}
                                onClick={(e) => goTo(e, n.id, n)}
                                >
                                <TableCell padding="checkbox" component="td" scope="row" className={classes.noPrint}>
                                    <Checkbox
                                        checked={isSelected(n.id)}
                                        onClick={() => handleSelect(n.id)}
                                        color="primary"
                                    />
                                </TableCell>
                                {columns.map(column => viewableColumns.indexOf(column.id) > -1 &&
                                    <TableCell
                                        component="td"
                                        key={column.id}
                                        align={column.align}
                                        padding={column.disablePadding ? 'none' : 'default'}
                                        className={column.wrap ? classes.tableCellWrap : classes.tableCell}
                                        >
                                        {getTableCell(column, n)}
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                        {(emptyRows > 0) &&
                            <TableRow style={{ height: 43 * emptyRows }}>
                                <TableCell colSpan={viewableColumns.length + 2} />
                            </TableRow>
                        }
                    </TableBody>
                </Table>
            </div>
            <TablePagination
                className={fullHeight ? classes.paginationFullHeight : null}
                count={total}
                component="div"
                rowsPerPage={rowsPerPage}
                page={page}
                backIconButtonProps={{
                    'aria-label': 'Previous Page',
                }}
                nextIconButtonProps={{
                    'aria-label': 'Next Page',
                }}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
                rowsPerPageOptions={[10,25,50,100,200]}
            />
            {EditDialogCloned}
            <iframe id="tablecontentstoprint" style={{height: 0, width: 0, position: 'absolute', display: 'none'}}></iframe>
        </div>
    );
}
